import React, { useEffect, useState } from 'react';
import { Form, FormInstance, Image, InputNumber, Radio, Select, Tag } from 'antd';
import { TbCash } from 'react-icons/tb';
import { CiBank } from 'react-icons/ci';
import { getBankShopQRCode } from '@app/api/bank.api';
import { getBanks } from '@app/store/slices/bankSlice';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { RootState } from '@app/store/store';
import { BankModel } from '@app/interfaces/bank';
import { formatNumberInInput } from '@app/utils/utils';

interface CustomerPaymentSectionProps {
  form: FormInstance;
}

// ── Shared class strings (migrated from Order.styles.css) ───────────────────

const SUMMARY_ROW_CLS = 'tw-flex tw-min-h-10 tw-flex-wrap tw-items-center tw-justify-between tw-gap-y-1 tw-py-0.5';
const SUMMARY_LABEL_CLS = 'tw-text-[13px] tw-text-on-surface-variant';
const INFO_BOX_CLS = 'tw-flex-1 tw-rounded-xl tw-border tw-border-solid tw-px-3 tw-py-2.5 tw-text-center';
const INFO_LABEL_CLS = 'tw-mb-1 tw-text-[11px] tw-font-medium tw-text-on-surface-variant';
const INFO_VALUE_CLS = 'tw-text-[15px] tw-font-bold';

// Nút chọn phương thức thanh toán: icon + nhãn đầy đủ, canh giữa, rộng thoáng
const paymentMethodBtnStyle: React.CSSProperties = {
  minWidth: 110,
  height: 38,
  lineHeight: '36px',
  textAlign: 'center',
  fontWeight: 600,
};

const paymentMethodIconStyle: React.CSSProperties = {
  fontSize: 16,
  marginRight: 6,
  verticalAlign: 'middle',
};

const CustomerPaymentSection: React.FC<CustomerPaymentSectionProps> = ({ form }) => {
  // Hiển thị danh sách ngân hàng + QR bám theo payment_type thực tế (mặc định CK
  // cho đơn mới; khi sửa đơn thì theo giá trị đã lưu). Dùng useWatch để luôn đồng bộ.
  const paymentType = Form.useWatch('payment_type', form);
  const showBankList = paymentType === 'bank_transfer';
  // "Đã thanh toán" (paid) vs "Thanh toán sau" (deferred). Phương thức thanh toán
  // (TM/CK + QR) hiển thị chung cho CẢ HAI trạng thái; chỉ phần "Khách thanh toán"
  // và kết quả công nợ là khác nhau.
  const isPaid = Form.useWatch('payment_status', form) === 'paid';
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showDebitValue, setShowDebitValue] = useState(true);
  const [refundAmount, setRefundAmount] = useState(0);
  const [selectedBank, setSelectedBank] = useState<string | undefined>();
  const [debtAmount, setDebtAmount] = useState(() => Number(form.getFieldValue('debit_amount')) || 0);

  // Đồng bộ lại công nợ/tiền thừa mỗi khi đổi trạng thái Đã thanh toán ↔ Thanh toán sau
  // (component không còn remount khi toggle nên phải sync theo isPaid).
  useEffect(() => {
    const total = Number(form.getFieldValue('total_amount')) || 0;
    const paid = Number(form.getFieldValue('customer_payment_amount')) || 0;
    setDebtAmount(Number(form.getFieldValue('debit_amount')) || 0);
    setRefundAmount(paid > total ? paid - total : 0);
    setShowDebitValue(true);
  }, [isPaid]);

  const { data: banks = [] } = useAppSelector((state: RootState) => state.bank || {});
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getBanks({ bank_type_eq: 0, active_true: true }));
  }, []);

  useEffect(() => {
    if (banks.length > 0) {
      const first = banks[0].account_number;
      setSelectedBank(first);
      generateQRCode(banks[0]);
    }
  }, [banks]);

  const generateQRCode = async (bank: BankModel) => {
    try {
      const totalAmount = Number(form.getFieldValue('total_amount')) || 0;
      const qr = await getBankShopQRCode(bank, totalAmount, 'Chuyen khoan');
      if (qr) setQrCodeUrl(qr);
    } catch {
      // silent — QR is non-critical
    }
  };

  const handlePaymentTypeChange = (e: any) => {
    const type = e.target.value;
    if (type === 'bank_transfer' && banks.length > 0) {
      generateQRCode(banks[0]);
    } else {
      setQrCodeUrl('');
    }
  };

  const handleCustomerPaymentChange = (value: number | null) => {
    const paid = Number(value) || 0;
    const total = Number(form.getFieldValue('total_amount')) || 0;
    const owed = paid > total ? 0 : total - paid;
    const debit = -owed;
    form.setFieldsValue({ debit_amount: debit });
    setDebtAmount(debit);
    setShowDebitValue(paid >= 0);
    setRefundAmount(paid > total ? paid - total : 0);
  };

  const handleBankChange = (value: string) => {
    const bank = banks.find((b) => b.account_number === value);
    if (bank) generateQRCode(bank);
  };

  const predictValues = (): number[] => {
    const total = Number(form.getFieldValue('total_amount')) || 0;
    const step = total < 500000 ? 500000 : total;
    const next = Math.ceil(total / step) * step + step;
    return [total, next];
  };

  const bankOptions = banks.map((b) => ({
    value: b.account_number,
    label: (
      <div style={{ padding: '2px 0' }}>
        <div>
          <strong>{b.account_number}</strong> - {b.name}
        </div>
        <div className="tw-text-xs tw-text-on-surface-variant">{b.holder_name}</div>
      </div>
    ),
  }));

  return (
    <div>
      {/* Customer payment amount — chỉ khi "Đã thanh toán" */}
      {isPaid && (
        <>
          <div className={SUMMARY_ROW_CLS} style={{ marginTop: 8 }}>
            <span className={SUMMARY_LABEL_CLS}>Khách thanh toán</span>
            <Form.Item name="customer_payment_amount" style={{ marginBottom: 0 }}>
              <InputNumber
                className="order-input-item text-primary"
                formatter={(v) => formatNumberInInput(v)}
                onChange={handleCustomerPaymentChange}
                inputMode="numeric"
                style={{ width: 160, fontWeight: 600 }}
                placeholder="Nhập số tiền"
              />
            </Form.Item>
          </div>

          {/* Quick-pick amounts */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, paddingLeft: 4 }}>
            {predictValues().map((val, i) => (
              <Tag
                key={i}
                color="var(--primary-color)"
                style={{ padding: '4px 10px', fontSize: '12px', cursor: 'pointer', margin: 0 }}
                onClick={() => {
                  form.setFieldsValue({ customer_payment_amount: val });
                  handleCustomerPaymentChange(val);
                }}
              >
                {formatNumberInInput(val)}
              </Tag>
            ))}
          </div>
        </>
      )}

      {/* Payment method radio — LUÔN hiển thị (dùng chung cả 2 trạng thái) */}
      <div className={SUMMARY_ROW_CLS}>
        <span className={SUMMARY_LABEL_CLS}>Phương thức</span>
        <Form.Item name="payment_type" style={{ marginBottom: 0 }}>
          <Radio.Group onChange={handlePaymentTypeChange} optionType="button" buttonStyle="solid" size="middle">
            <Radio.Button value="cash" style={paymentMethodBtnStyle}>
              <TbCash style={paymentMethodIconStyle} />
              Tiền mặt
            </Radio.Button>
            <Radio.Button value="bank_transfer" style={paymentMethodBtnStyle}>
              <CiBank style={paymentMethodIconStyle} />
              Chuyển khoản
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>

      {/* Bank selector + QR code */}
      {showBankList && (
        <div style={{ background: 'rgba(0, 76, 76, 0.06)', borderRadius: 6, padding: 10, marginTop: 6 }}>
          <Select
            showSearch
            placeholder="Chọn tài khoản ngân hàng"
            onChange={handleBankChange}
            options={bankOptions}
            defaultValue={selectedBank}
            style={{ width: '100%', marginBottom: 10 }}
          />
          {qrCodeUrl && (
            <div className="tw-bg-surface-container-lowest" style={{ textAlign: 'center', padding: 10, borderRadius: 6 }}>
              <Image width={150} src={qrCodeUrl} alt="QR Code" style={{ borderRadius: 6 }} />
            </div>
          )}
        </div>
      )}

      {/* Kết quả công nợ */}
      {isPaid ? (
        // "Đã thanh toán": công nợ còn lại + tiền thừa (nếu có)
        showDebitValue && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {refundAmount > 0 && (
              <div className={`${INFO_BOX_CLS} tw-border-[#ffd591] tw-bg-[#fff7e6]`}>
                <div className={INFO_LABEL_CLS}>Tiền thừa</div>
                <div className={`${INFO_VALUE_CLS} tw-text-warning`}>{formatNumberInInput(refundAmount)}</div>
              </div>
            )}
            <div className={`${INFO_BOX_CLS} tw-border-[#ffa39e] tw-bg-[#fff1f0]`}>
              <div className={INFO_LABEL_CLS}>Công nợ</div>
              <div className={`${INFO_VALUE_CLS} tw-text-error`}>{formatNumberInInput(debtAmount)}</div>
            </div>
          </div>
        )
      ) : (
        // "Thanh toán sau": toàn bộ giá trị đơn là công nợ khách hàng
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <div className={`${INFO_BOX_CLS} tw-border-[#ffa39e] tw-bg-[#fff1f0]`} style={{ flex: 1 }}>
            <div className={INFO_LABEL_CLS}>Công nợ khách hàng</div>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.total_amount !== curr.total_amount}>
              {({ getFieldValue }) => {
                const total = Number(getFieldValue('total_amount') || 0);
                return (
                  <div className={`${INFO_VALUE_CLS} tw-text-error`}>
                    {total > 0 ? `-${formatNumberInInput(total)}` : formatNumberInInput(total)}
                  </div>
                );
              }}
            </Form.Item>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPaymentSection;
